import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import firebase from 'firebase/compat/app'
import { getLogger } from '@shared/logging';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AuthNZService } from '../core/authnz.service';


const log = getLogger("SessionRevocationService")

type RevocationKey = {
  revocationKey: string;
  timestamp: number;
}


@Injectable({
  providedIn: 'root'
})
export class SessionRevocationService {
  private subscription: Subscription;
  /**
  * 
  */
  private revocationKeyError: boolean;

  private revocationKey = new BehaviorSubject<RevocationKey>(null);

  private currentToken: firebase.auth.IdTokenResult;
  private currentUser = new BehaviorSubject<firebase.UserInfo>(null)

  constructor(
    private afs: AngularFirestore,
    private afa: AngularFireAuth,
    private authnz: AuthNZService
  ) {
    this.afa.authState.subscribe((state)=>{
      this.currentUser.next(state)
      if (state?.uid) {
        this.subscription?.unsubscribe();
        this.watchForRevocation();
      }
      else {
        this.subscription?.unsubscribe();
      }
    })

    /*this.visibilityService.pageVisiblity$.pipe(
      filter(v=>v.focused && v.visible)
    ).subscribe(
      (v)=>{
        this.validateUserSession();
      },
      (err)=>{
        log.warn("Error watching pageVisibility",err)
      }
    );*/
   }

  private watchForRevocation() {
    log.debug("Watching for token revocations of uid " + this.currentUser.value.uid);
    this.subscription = this.afs.doc("revocationKeys/"+ this.currentUser.value.uid)
      .snapshotChanges().subscribe(
        (snap)=>{
          const newKey = snap.payload.data() as RevocationKey;
          log.debug("RevocationKey change", newKey);
          this.revocationKey.next(newKey)
          this.validateUserSession();
        },
        (err)=>{
          log.warn("Error watching for revocationKey change",err)
        },
        ()=>{
          log.debug("Stopped watching for token revocations of uid " + this.currentUser.value.uid);
        }
      );
  }


    /**
   * Checks if the current user's session is still valid
   * and whether any server-side revocations have ocurred.
   * 
   * @returns boolean whether or not the session is valid
   */
    private async validateUserSession(): Promise<boolean> {
      try { 
        this.currentToken = await firebase.auth().currentUser.getIdTokenResult();
      }
      catch (err) {
        log.warn("Failed getting current token; it's likely expired and could not be renewed", err)
        this.authnz.logout();
        return false;
      }
      const validationResult = await this.validateRevocationKey();
      if (!validationResult) {
        return false;
      }
      
      log.debug("User session is still valid.  Logged in at " + this.currentToken.authTime);
      return true;
    }

    private async validateRevocationKey() {
      if (!this.revocationKey.value) {
        // There's no revocation key for the current user, so there's nothing to validate
        return true;
      }

      if (this.revocationKey.value.revocationKey == this.currentToken.claims.revocationKey) {
        // The current user's revocationKey matches the claims on their auth token, so they're valid.
        return true;
      }

      log.warn("Current auth token claims do not match the user's revocationKey ");

      let keyValid = false;

      try {
        keyValid = await this.refreshClaimsTokenAndCheckRevocation();
      }
      catch (err) {
        // Renewing the token failed, so Fireauth knows this user's refreshtoken was revoked
        log.warn("The user's authentication token was revoked.")
        this.authnz.logout();
        return false;
      }
      
      if (!keyValid) {
        log.warn("The revocationKey is invalid for the current user")
        this.revocationKeyError = true;
        return false;
      
      }
    }

    private async refreshClaimsTokenAndCheckRevocation(): Promise<boolean> {
      let r: firebase.auth.IdTokenResult
      r = await firebase.auth().currentUser.getIdTokenResult(true);      
      log.debug("Renewed token after revocation", r)
      let newTokenValid = r.claims.revocationKey == this.revocationKey.value.revocationKey
      if (!newTokenValid) {
        /**
         * Fireauth issued us a new token, but the `revocationKey` claim 
         * still doesn't match the value on the User document
         * 
         * This shouldn't happen.
         */ 
        log.warn("The new revocationKey is still invalid for the current user")
      }
      return newTokenValid;
    }
}
